import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { BasicMemberFetchStarted, LoadMember, MemberActions, MemberFetchStarted, MemberLoaded } from './member-actions';
import { EMPTY, Observable } from 'rxjs';
import { first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '../reducers';
import { MemberService } from '@app/core/members/member.service';
import { MemberInterface } from '@app/core/_models/member-interface';
import { MemberIdKeeperService } from '@app/core/member-id-keeper.service';
import { getCookieConsentStatus$ } from '../core-context/core-context-helpers';
import { CookieConsentService, COOKIES_CONSENT_KEY } from '@app/core/cookie-consent/cookie-consent.service';
import { EmptyAction } from '../actions';
import { SessionService } from '@app/core/session/session.service';
import { logMsg } from '@app/shared/utils/logger';
import * as Sentry from '@sentry/angular';
import { isAfter, isBefore } from 'date-fns';

@Injectable()
export class MemberEffects {

  public loadMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.LOAD_MEMBER),
    withLatestFrom(this.store.pipe(select(fromRoot.selectMember), first())),
    switchMap(([action, memberStore]) => {
      const loadMemberAction = <LoadMember>action;
      if (memberStore.isMemberFetched && !loadMemberAction.force) { return EMPTY; }

      return this.fetchMember();
    }),
    tap(() => this.store.dispatch(new MemberFetchStarted())),
    map((m) => new MemberLoaded(m)),
  ));

  public loadBasicMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.LOAD_BASIC_MEMBER),
    withLatestFrom(this.store.pipe(select(fromRoot.selectMember), first())),
    switchMap(([_action, memberStore]) => {
      if (memberStore.isBasicMemberFetched) { return EMPTY; }
      return this.fetchBasicMember();
    }),
    tap(() => this.store.dispatch(new BasicMemberFetchStarted())),
    map((m) => new MemberLoaded(m)),
  ));

  public memberLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.MEMBER_LOADED),
    tap((action) => this.onMemberLoaded((<Partial<MemberLoaded>>action)?.member)),
    map(() => new EmptyAction()),
  ));

  public clearMember$ = createEffect(() => this.actions$.pipe(
    ofType(MemberActions.CLEAR_MEMBER),
    tap(() => this.memberIdKeeper.clearMemberId()),
    map(() => new EmptyAction()),
  ));

  constructor(public actions$: Actions,
              private store: Store<fromRoot.State>,
              private memberService: MemberService,
              private memberIdKeeper: MemberIdKeeperService,
              private cookieConsentService: CookieConsentService,
              private sessionService: SessionService) {}

  private fetchMember(): Observable<Partial<MemberInterface> | null> {
    return this.memberService.fetchMember();
  }

  private fetchBasicMember(): Observable<Partial<MemberInterface> | null> {
    return this.sessionService.basicAutologinInfo();
  }

  /**
   * Callback fired after member is logged in.
   * @param member Logged member
   */
  private onMemberLoaded(member: Partial<MemberInterface> | undefined | null): void {
    if (!member) { return; }

    const memberId = member?.id;
    if (memberId) {
      this.memberIdKeeper.saveMemberId(memberId);
      Sentry.withScope((scope) => {
        scope.setUser({ id: memberId?.toString() });
      });
    }
    this.dispatchCookieConsent(member);
  }

  private dispatchCookieConsent(member): void {
    const memberConsent = member.consents[COOKIES_CONSENT_KEY];

    getCookieConsentStatus$(this.store).pipe(first()).subscribe((consentStatus) => {
      if (this.shouldUpdateBrowserConsent(memberConsent, consentStatus)) {
        // when banner not clicked but cookie_tracking is set on member
        logMsg(MemberEffects, 'Updating browser consent');
        this.cookieConsentService.toggle(memberConsent.status, false);
        return; // stop flow
      }

      if (consentStatus != null) {
        if (this.shouldUpdateMemberConsent(memberConsent, consentStatus)) {
          // when banner clicked but different consent is given on member
          logMsg(MemberEffects, 'Updating member consent');
          this.cookieConsentService.toggle(consentStatus, true);
        }
      }
    });
  }

  private shouldUpdateBrowserConsent(memberConsent, isBrowserConsentGiven: boolean | null): boolean {
    // when there is no member consent
    if (memberConsent == null) { return false; }
    // when consent on browser not "given" and exists on member
    if (isBrowserConsentGiven == null && memberConsent.status != null) { return true; }
    // when consent on member is given (or changed) AFTER the one in browser
    const lastUpdateDate = this.cookieConsentService.getLastUpdateDate();
    return memberConsent.status !== isBrowserConsentGiven && !!lastUpdateDate
      && !!memberConsent.updated_at && isBefore(lastUpdateDate, memberConsent.updated_at);
  }

  private shouldUpdateMemberConsent(memberConsent, browserConsentStatus: boolean): boolean {
    // this will only fire when consent on browser is already given!

    // when there is no member consent
    if (memberConsent == null) { return true; }
    // when consent on member doesn't match browser status
    if (memberConsent.status !== browserConsentStatus) {
      // when there is no saved lastUpdated dates
      const lastUpdateDate = this.cookieConsentService.getLastUpdateDate();
      if (!memberConsent.updated_at || !lastUpdateDate) { return true; }
      // when consent on browser is given after consent on member
      if (lastUpdateDate && isAfter(lastUpdateDate, memberConsent.updated_at)) { return true; }
    }

    return false;
  }
}
